import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import MetaTags from "react-meta-tags";
import { withRouter } from "react-router-dom";

import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Input,
  Table,
} from "reactstrap";

import Breadcrumbs from "components/Common/Breadcrumb";

import { getStockList } from "store/scheme/actions";

const API_BASE = "https://externalqcapi.com/api";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// yyyy-mm-dd -> DD-MMM-YY (parsed manually so timezones never shift the day)
const formatDate = value => {
  if (!value || value === "-") return "-";

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));

  if (!match) return String(value);

  const [, year, month, day] = match;
  const monthName = MONTHS[Number(month) - 1];

  if (!monthName) return String(value);

  return `${day}-${monthName}-${year.slice(-2)}`;
};

const dateOnly = v => (v ? String(v).slice(0, 10) : "");

const absQty = v => Math.abs(Number(v) || 0);

// Today as yyyy-mm-dd in the user's LOCAL time zone.
const todayStr = () => {
  const d = new Date();

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

const PAGE_STYLES = `
  .so-card {
    border: 1px solid #e3eaee;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(20, 40, 60, 0.06);
  }
  .so-stat {
    border: 1px solid #e3eaee;
    border-radius: 10px;
    padding: 12px 16px;
    background: #fff;
    height: 100%;
  }
  .so-stat-label {
    font-size: 12.5px;
    color: #6b7c8a;
  }
  .so-stat-value {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
    color: #1f2d3d;
  }
  .so-stat-out .so-stat-value {
    color: #c0392b;
  }
  .so-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
  }
  .so-head h5 {
    margin: 0;
    font-size: 16px;
    color: #0b5f67;
  }
  .so-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    background: #e8f4f5;
    color: #0b5f67;
    font-size: 12.5px;
    font-weight: 600;
  }
  .so-chip-out {
    background: #fdecea;
    color: #b0301f;
  }
  .so-scroll {
    max-height: 340px;
    overflow: auto;
    border: 1px solid #e3eaee;
    border-radius: 8px;
  }
  .so-table {
    font-size: 13.5px;
    margin-bottom: 0;
  }
  .so-table thead th {
    background: #f4f7f9;
    color: #51606c;
    font-weight: 600;
    border-bottom: 1px solid #dfe6ea;
    padding: 7px 10px;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .so-table td {
    padding: 6px 10px;
    vertical-align: middle;
    border-color: #eef2f4;
  }
  .so-table tr.so-date-first td {
    border-top: 1px solid #dfe6ea;
  }
  .so-date-cell {
    font-weight: 600;
    color: #1f2d3d;
    white-space: nowrap;
  }
  .so-open td {
    background: #e8f4f5;
    color: #0b5f67;
    font-weight: 700;
  }
  .so-qty {
    display: inline-block;
    min-width: 40px;
    padding: 1px 8px;
    border-radius: 999px;
    background: #fdecea;
    color: #b0301f;
    font-weight: 700;
    text-align: center;
  }
  .so-balance {
    font-weight: 700;
    color: #1f2d3d;
    font-size: 14.5px;
  }
  .so-balance-low {
    color: #c0392b;
  }
  .so-empty {
    text-align: center;
    color: #6b7c8a;
    padding: 36px 8px;
  }
`;

class StockOut extends Component {
  constructor(props) {
    super(props);

    this._mounted = false;

    const authUser = localStorage.getItem("authUser");
    const parsed = authUser ? JSON.parse(authUser) : {};

    this.state = {
      user_id: parsed.user_id || "",
      token: parsed.token || "",
      dcList: [], // every delivery challan line = one stock out entry
      loading: false,
      error: "",

      // Filters
      filterDate: "", // empty = all dates
      filterScheme: "", // empty = all schemes
      search: "", // DC No contains...

      // false: opening stock on top, balance counts down (600, 599, 598...)
      newestFirst: false,
    };
  }

  componentDidMount() {
    this._mounted = true;
    this.props.onGetStock(this.state.user_id); // current remaining stock
    this.fetchDcList();
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  // Same endpoint the Delivery Challan page uses, without the participant
  // filter -> every DC line of every participant.
  fetchDcList = async () => {
    const { token } = this.state;

    this.setState({ loading: true, error: "" });

    try {
      const response = await fetch(
        `${API_BASE}/database-admin/delivery-challans/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || data.error || "Could not load delivery challans."
        );
      }

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : [];

      if (this._mounted) {
        this.setState({ dcList: list, loading: false });
      }
    } catch (err) {
      if (this._mounted) {
        this.setState({
          loading: false,
          error: err.message || "Could not load delivery challans.",
        });
      }
    }
  };

  // Stock remaining NOW per scheme (all lots added together).
  getRemainingNow = stockList => {
    const remainingNow = {};

    stockList.forEach(s => {
      const key = s.name || s.scheme_name;

      remainingNow[key] = (remainingNow[key] || 0) + (Number(s.quantity) || 0);
    });

    return remainingNow;
  };

  // Adds "balance" = stock left for that scheme right AFTER this entry.
  //
  // The backend already deducts stock when a DC is created, so the Stock In
  // list holds the stock remaining NOW. Walking from the newest entry back
  // to the oldest and adding the later dispatches back gives the balance at
  // every point in history.
  buildRows = (dcList, remainingNow) => {
    // oldest -> newest
    const asc = [...dcList].sort(
      (a, b) =>
        dateOnly(a.dispatch_date).localeCompare(dateOnly(b.dispatch_date)) ||
        String(a.dc_no).localeCompare(String(b.dc_no)) ||
        Number(a.id || 0) - Number(b.id || 0)
    );

    const laterOut = {};
    const result = [];

    for (let i = asc.length - 1; i >= 0; i -= 1) {
      const item = asc[i];
      const key = item.scheme_name;

      result.push({
        ...item,
        _key: `${item.dc_no}-${item.id ?? i}`,
        balance: (remainingNow[key] || 0) + (laterOut[key] || 0),
      });

      laterOut[key] = (laterOut[key] || 0) + absQty(item.quantity);
    }

    // newest -> oldest
    return result;
  };

  // rows (newest -> oldest) -> one ledger per scheme, entries oldest -> newest
  buildLedgers = rows => {
    const bySchemes = new Map();

    rows.forEach(r => {
      const key = r.scheme_name || "-";

      if (!bySchemes.has(key)) bySchemes.set(key, []);

      bySchemes.get(key).push(r);
    });

    return [...bySchemes.entries()]
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
      .map(([name, newestToOldest]) => {
        const entries = [...newestToOldest].reverse();
        const first = entries[0];

        return {
          name,
          entries,
          // Stock before the first entry that is shown.
          opening: first.balance + absQty(first.quantity),
          totalOut: entries.reduce((sum, e) => sum + absQty(e.quantity), 0),
        };
      });
  };

  clearFilters = () =>
    this.setState({ filterDate: "", filterScheme: "", search: "" });

  refresh = () => {
    this.props.onGetStock(this.state.user_id);
    this.fetchDcList();
  };

  renderLedger = ledger => {
    const { newestFirst, filterDate } = this.state;

    const remainingNow = this.remainingNow || {};
    const current = remainingNow[ledger.name];

    const ordered = newestFirst ? [...ledger.entries].reverse() : ledger.entries;

    const openingRow = (
      <tr className="so-open" key="opening">
        <td colSpan={4}>
          {filterDate ? "Stock before this date" : "Opening stock"}
        </td>
        <td className="text-center">{ledger.opening}</td>
      </tr>
    );

    return (
      <Card className="so-card mb-3" key={ledger.name}>
        <CardBody>
          <div className="so-head">
            <h5>{ledger.name}</h5>

            <div className="d-flex gap-2 flex-wrap">
              <span className="so-chip so-chip-out">
                Out: -{ledger.totalOut}
              </span>
              {current !== undefined && (
                <span className="so-chip">Stock now: {current}</span>
              )}
            </div>
          </div>

          <div className="so-scroll">
            <Table className="so-table" responsive={false}>
              <thead>
                <tr>
                  <th style={{ width: "17%" }}>Date</th>
                  <th style={{ width: "19%" }}>DC No</th>
                  <th style={{ width: "22%" }} className="text-center">
                    Lot No
                  </th>
                  <th style={{ width: "14%" }} className="text-center">
                    Out
                  </th>
                  <th style={{ width: "14%" }} className="text-center">
                    Stock
                  </th>
                </tr>
              </thead>

              <tbody>
                {!newestFirst && openingRow}

                {ordered.map((e, i) => {
                  const prev = ordered[i - 1];
                  const sameDate =
                    prev &&
                    dateOnly(prev.dispatch_date) === dateOnly(e.dispatch_date);

                  return (
                    <tr
                      key={e._key}
                      className={!sameDate && i > 0 ? "so-date-first" : ""}
                    >
                      <td className="so-date-cell">
                        {sameDate ? "" : formatDate(e.dispatch_date)}
                      </td>
                      <td>{e.dc_no}</td>
                      <td className="text-center">{e.lot_no || "-"}</td>
                      <td className="text-center">
                        <span className="so-qty">-{absQty(e.quantity)}</span>
                      </td>
                      <td
                        className={`text-center so-balance ${
                          e.balance <= 0 ? "so-balance-low" : ""
                        }`}
                      >
                        {e.balance}
                      </td>
                    </tr>
                  );
                })}

                {newestFirst && openingRow}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    );
  };

  render() {
    const { StockList } = this.props;
    const {
      filterDate,
      filterScheme,
      search,
      dcList,
      loading,
      error,
      newestFirst,
    } = this.state;

    const stockList = Array.isArray(StockList) ? StockList : [];

    // Kept on the instance so renderLedger can show "Stock now".
    this.remainingNow = this.getRemainingNow(stockList);

    // Balances are always worked out on ALL entries, then filtered.
    const allRows = this.buildRows(dcList, this.remainingNow);

    const schemeNames = [
      ...new Set(dcList.map(d => d.scheme_name).filter(Boolean)),
    ].sort((a, b) => String(a).localeCompare(String(b)));

    const q = search.trim().toLowerCase();

    const rows = allRows.filter(
      r =>
        (!filterDate || dateOnly(r.dispatch_date) === filterDate) &&
        (!filterScheme || r.scheme_name === filterScheme) &&
        (!q || String(r.dc_no).toLowerCase().includes(q))
    );

    const ledgers = this.buildLedgers(rows);

    const totalOut = rows.reduce((sum, r) => sum + absQty(r.quantity), 0);
    const dcCount = new Set(rows.map(r => r.dc_no)).size;

    const filtersActive = Boolean(filterDate || filterScheme || q);

    return (
      <React.Fragment>
        <div className="page-content">
          <MetaTags>
            <title>Stock Out | NEQAS</title>
          </MetaTags>

          <style>{PAGE_STYLES}</style>

          <Container fluid>
            <Breadcrumbs title="Stock" breadcrumbItem="Stock Out" />

            {/* Summary */}
            <Row className="g-3 mb-3">
              <Col sm="4">
                <div className="so-stat so-stat-out">
                  <div className="so-stat-label">
                    {filtersActive ? "Total out (filtered)" : "Total out"}
                  </div>
                  <div className="so-stat-value">-{totalOut}</div>
                </div>
              </Col>
              <Col sm="4">
                <div className="so-stat">
                  <div className="so-stat-label">Delivery challans</div>
                  <div className="so-stat-value">{dcCount}</div>
                </div>
              </Col>
              <Col sm="4">
                <div className="so-stat">
                  <div className="so-stat-label">Schemes</div>
                  <div className="so-stat-value">{ledgers.length}</div>
                </div>
              </Col>
            </Row>

            {/* Filters */}
            <Card className="so-card mb-3">
              <CardBody className="py-3">
                <Row className="g-2 align-items-center">
                  <Col md="3">
                    <Input
                      type="search"
                      bsSize="sm"
                      placeholder="Search DC No"
                      value={search}
                      onChange={e => this.setState({ search: e.target.value })}
                    />
                  </Col>

                  <Col md="3">
                    <Input
                      type="select"
                      bsSize="sm"
                      value={filterScheme}
                      onChange={e =>
                        this.setState({ filterScheme: e.target.value })
                      }
                    >
                      <option value="">All schemes</option>
                      {schemeNames.map(name => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col md="3">
                    <div className="d-flex gap-1">
                      <Input
                        type="date"
                        bsSize="sm"
                        value={filterDate}
                        onChange={e =>
                          this.setState({ filterDate: e.target.value })
                        }
                      />
                      <Button
                        size="sm"
                        color="light"
                        onClick={() => this.setState({ filterDate: todayStr() })}
                      >
                        Today
                      </Button>
                    </div>
                  </Col>

                  <Col md="3" className="text-md-end">
                    <Button
                      size="sm"
                      color="light"
                      className="me-1"
                      onClick={() =>
                        this.setState({ newestFirst: !newestFirst })
                      }
                    >
                      <i
                        className={`mdi ${
                          newestFirst ? "mdi-sort-descending" : "mdi-sort-ascending"
                        } me-1`}
                      />
                      {newestFirst ? "Newest first" : "Oldest first"}
                    </Button>

                    {filtersActive && (
                      <Button
                        size="sm"
                        color="link"
                        className="me-1"
                        onClick={this.clearFilters}
                      >
                        Clear
                      </Button>
                    )}

                    <Button
                      size="sm"
                      color="secondary"
                      outline
                      disabled={loading}
                      onClick={this.refresh}
                    >
                      <i className="mdi mdi-refresh me-1" />
                      {loading ? "Loading..." : "Refresh"}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* One ledger card per scheme */}
            {ledgers.map(this.renderLedger)}

            {!ledgers.length && (
              <Card className="so-card">
                <CardBody>
                  <div className={`so-empty ${error ? "text-danger" : ""}`}>
                    {loading
                      ? "Loading stock out records..."
                      : error ||
                        (filtersActive
                          ? "Nothing matches these filters."
                          : "No stock out records yet.")}
                  </div>
                </CardBody>
              </Card>
            )}
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

StockOut.propTypes = {
  match: PropTypes.object,
  StockList: PropTypes.array,
  onGetStock: PropTypes.func,
};

// StockList lives under the same "SchemeList" reducer slice as on Stock In
const mapStateToProps = ({ SchemeList }) => ({
  StockList: SchemeList.StockList,
});

const mapDispatchToProps = dispatch => ({
  onGetStock: id => dispatch(getStockList(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(StockOut));